import { extractListItems } from 'core/utils/apollo/extractListItems';
import { useGetAnalysedCandidatesByCandidateBatchIdLazyQuery } from 'generated-graphql/hooks/apollo-hooks';
import { useEffect, useState } from 'react';

type AnalysedApplicationItems = {
  candidateBatchId: string;
  candidateProfilesIds: string[];
};

type FetchPageProps = {
  candidateBatchId: string;
  nextToken?: string;
  accumulatedItems: string[];
};

type UseFetchAllAnalysedApplicationsByCandidateBatchProps = {
  candidateBatchId: string | null;
};

export const useFetchAllAnalysedApplicationsByCandidateBatch = ({
  candidateBatchId
}: UseFetchAllAnalysedApplicationsByCandidateBatchProps) => {
  const [getAnalysedCandidatesByCandidateBatchIdLazyQuery, { loading }] =
    useGetAnalysedCandidatesByCandidateBatchIdLazyQuery();
  const [analysedApplications, setAnalysedApplications] =
    useState<AnalysedApplicationItems>();

  const fetchPage = async ({
    candidateBatchId,
    nextToken,
    accumulatedItems
  }: FetchPageProps): Promise<string[]> => {
    const result = await getAnalysedCandidatesByCandidateBatchIdLazyQuery({
      variables: {
        candidateBatchId,
        nextToken
      }
    });

    const items = extractListItems(
      result.data?.getCandidatesByCandidatesBatch?.items ?? []
    )
      .filter((item) => !!item.candidateProfileId)
      // We can safely cast here because we know the item is not null
      .map((item) => item.candidateProfileId as string);

    // If no items were returned, stop pagination
    if (items.length === 0) {
      return accumulatedItems;
    }

    const allItems = [...accumulatedItems, ...items];
    const newNextToken = result.data?.getCandidatesByCandidatesBatch?.nextToken;

    // Continue if there's a next token
    if (newNextToken) {
      return fetchPage({
        candidateBatchId,
        nextToken: newNextToken,
        accumulatedItems: allItems
      });
    }

    return allItems;
  };

  useEffect(() => {
    // Capture the current candidateBatchId to detect stale requests
    const activeId = candidateBatchId;

    const fetchAllAnalysedApplications = async () => {
      if (!activeId) {
        setAnalysedApplications(undefined);
        return;
      }

      if (activeId === analysedApplications?.candidateBatchId) {
        return;
      }

      const results = await fetchPage({
        candidateBatchId: activeId,
        accumulatedItems: []
      });

      // Only update state if this is still the current candidateBatchId
      if (activeId === candidateBatchId) {
        setAnalysedApplications({
          candidateBatchId: activeId,
          candidateProfilesIds: results
        });
      }
    };

    fetchAllAnalysedApplications();
  }, [candidateBatchId, analysedApplications?.candidateBatchId]);

  return {
    analysedApplications,
    loading
  };
};
